const MIME_TO_EXTENSION = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getOutputName = (originalName, mimeType) => {
  const baseName = (originalName || 'player-image').replace(/\.[^/.]+$/, '');
  const extension = MIME_TO_EXTENSION[mimeType] || 'webp';
  return `${baseName}.${extension}`;
};

const loadImageFile = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image file.'));
    };

    image.src = objectUrl;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

const getScaledDimensions = (width, height, maxWidth, maxHeight) => {
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
};

export const processPlayerImage = async (file, options = {}) => {
  const {
    maxWidth = 512,
    maxHeight = 512,
    outputType = 'image/webp',
    quality = 0.82,
    minQuality = 0.6,
    qualityStep = 0.08,
    maxOutputBytes = 3 * 1024 * 1024,
    maxDimensionShrinks = 2,
  } = options;

  const image = await loadImageFile(file);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) {
    throw new Error('Canvas 2D context unavailable.');
  }

  let { width: targetWidth, height: targetHeight } = getScaledDimensions(
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    maxWidth,
    maxHeight
  );

  let currentType = outputType;
  let currentQuality = clamp(quality, minQuality, 1);
  let outputBlob = null;

  for (let shrinkAttempt = 0; shrinkAttempt <= maxDimensionShrinks; shrinkAttempt += 1) {
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    context.clearRect(0, 0, targetWidth, targetHeight);
    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    let candidateQuality = currentQuality;
    for (let encodeAttempt = 0; encodeAttempt < 8; encodeAttempt += 1) {
      let blob = await canvasToBlob(canvas, currentType, candidateQuality);

      if (!blob && currentType === 'image/webp') {
        currentType = 'image/jpeg';
        candidateQuality = Math.max(candidateQuality, 0.8);
        blob = await canvasToBlob(canvas, currentType, candidateQuality);
      }

      if (!blob) {
        throw new Error('Failed to encode processed image.');
      }

      outputBlob = blob;
      if (blob.size <= maxOutputBytes || candidateQuality <= minQuality) {
        currentQuality = candidateQuality;
        break;
      }

      candidateQuality = Math.max(minQuality, candidateQuality - qualityStep);
      currentQuality = candidateQuality;
    }

    if (outputBlob && outputBlob.size <= maxOutputBytes) {
      break;
    }

    targetWidth = Math.max(128, Math.round(targetWidth * 0.85));
    targetHeight = Math.max(128, Math.round(targetHeight * 0.85));
  }

  if (!outputBlob) {
    throw new Error('Unable to process image.');
  }

  if (outputBlob.size > maxOutputBytes) {
    throw new Error('Processed image exceeds size limit.');
  }

  return new File([outputBlob], getOutputName(file.name, outputBlob.type || currentType), {
    type: outputBlob.type || currentType,
    lastModified: Date.now(),
  });
};
